import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import styles from './CertificateApprovalSection.module.scss';
import { ConfigSection } from './ConfigSection';
import sharedStyles from './TenantConfigTab.module.scss';
import {
  listTenantCertificateTypeConfigs,
  updateTenantCertificateTypeConfig,
  updateTenantConfig,
  type CertificateRequestType,
  type CertificateTypeConfig,
} from '../api/tenants.graphql';

interface Props {
  tenantId: string;
  autoApproveLaborCertificates: boolean;
  onUpdated: () => void;
}

/** Etiquetas conocidas; un tipo nuevo del enum se muestra con su código hasta tener una. */
const CERTIFICATE_TYPE_LABELS: Readonly<Record<string, string>> = {
  LABORAL: 'Certificado laboral',
};

function typeLabel(type: CertificateRequestType): string {
  return CERTIFICATE_TYPE_LABELS[type] ?? type;
}

function toManualMap(configs: CertificateTypeConfig[]): Record<CertificateRequestType, boolean> {
  return Object.fromEntries(configs.map((c) => [c.type, c.requiresManualApproval]));
}

/**
 * OS-1519 — certificados laborales automáticos. Un certificado se genera sin aprobación de
 * Nómina solo si el tenant tiene la auto-aprobación activa y su tipo no requiere aprobación manual.
 * Los tipos vienen de la API (todos los valores del enum, con default manual si no hay fila).
 */
export function CertificateApprovalSection({
  tenantId,
  autoApproveLaborCertificates,
  onUpdated,
}: Props): ReactElement {
  const typesQuery = useQuery({
    queryKey: ['platform-tenant-certificate-types', tenantId],
    queryFn: () => listTenantCertificateTypeConfigs(tenantId),
  });

  const [autoApprove, setAutoApprove] = useState(autoApproveLaborCertificates);
  const [manualByType, setManualByType] = useState<Record<CertificateRequestType, boolean>>({});
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setAutoApprove(autoApproveLaborCertificates);
  }, [autoApproveLaborCertificates]);

  useEffect(() => {
    if (typesQuery.data) {
      setManualByType(toManualMap(typesQuery.data));
    }
  }, [typesQuery.data]);

  const savedManualByType = useMemo(() => toManualMap(typesQuery.data ?? []), [typesQuery.data]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (autoApprove !== autoApproveLaborCertificates) {
        await updateTenantConfig(tenantId, { autoApproveLaborCertificates: autoApprove });
      }
      const changedTypes = Object.keys(manualByType).filter(
        (type) => manualByType[type] !== savedManualByType[type],
      );
      for (const type of changedTypes) {
        await updateTenantCertificateTypeConfig(tenantId, type, manualByType[type]);
      }
    },
    onSuccess: () => {
      setMessage('Configuración de certificados guardada.');
      void typesQuery.refetch();
      onUpdated();
    },
    onError: (err: Error) => setMessage(err.message),
  });

  const types = typesQuery.data ?? [];

  return (
    <ConfigSection
      title="Certificados laborales"
      description="Generación automática del certificado al solicitarlo desde Autogestión, sin aprobación de Nómina."
      footer={
        <>
          <button
            type="button"
            className={sharedStyles.primaryBtn}
            disabled={saveMutation.isPending || !typesQuery.data}
            onClick={() => {
              setMessage(null);
              saveMutation.mutate();
            }}
          >
            {saveMutation.isPending ? 'Guardando…' : 'Guardar certificados'}
          </button>
          {message ? <span className={sharedStyles.feedback}>{message}</span> : null}
        </>
      }
    >
      <div className={styles.rule}>
        Un certificado se genera automáticamente solo si se cumplen las dos condiciones:
        <ol>
          <li>La auto-aprobación está habilitada en el tenant.</li>
          <li>Su tipo no requiere aprobación manual.</li>
        </ol>
        En cualquier otro caso sigue el flujo de aprobación de Nómina.
      </div>

      <label className={sharedStyles.switchRow}>
        <span className={sharedStyles.switchCopy}>
          <strong>Habilitar auto-aprobación de certificados</strong>
          <span className={sharedStyles.hint}>autoApproveLaborCertificates</span>
        </span>
        <span className={sharedStyles.switch}>
          <input
            type="checkbox"
            checked={autoApprove}
            onChange={(e) => setAutoApprove(e.target.checked)}
          />
          <span className={sharedStyles.track} aria-hidden />
        </span>
      </label>

      <h4 className={styles.typesHeading}>Tipos de certificado</h4>
      {typesQuery.isLoading ? <p className={styles.status}>Cargando tipos…</p> : null}
      {typesQuery.isError ? (
        <p className={styles.error}>No se pudieron cargar los tipos de certificado.</p>
      ) : null}
      <ul className={styles.typeList}>
        {types.map(({ type }) => {
          const requiresManual = manualByType[type] ?? true;
          const isAutomatic = autoApprove && !requiresManual;
          return (
            <li key={type}>
              <label className={sharedStyles.switchRow}>
                <span className={sharedStyles.switchCopy}>
                  <strong>{typeLabel(type)} · Requiere aprobación manual</strong>
                  <span
                    className={`${styles.effective} ${
                      isAutomatic ? styles.effectiveAuto : styles.effectiveManual
                    }`}
                  >
                    {isAutomatic ? 'Resultado: automático' : 'Resultado: aprobación de Nómina'}
                  </span>
                </span>
                <span className={sharedStyles.switch}>
                  <input
                    type="checkbox"
                    aria-label={`${typeLabel(type)}: requiere aprobación manual`}
                    checked={requiresManual}
                    onChange={(e) =>
                      setManualByType((prev) => ({ ...prev, [type]: e.target.checked }))
                    }
                  />
                  <span className={sharedStyles.track} aria-hidden />
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </ConfigSection>
  );
}
